import { useState, useEffect, useCallback, useRef } from 'react'
import { Transition, Dialog } from '@headlessui/react'
import { useDropzone } from 'react-dropzone'
import { Captures } from '../api'
import { commonName } from '../speciesCommonNames'
import type { CaptureSummary, CaptureDetail, CaptureAnnotation, CaptureLabelSchema, MediaSchema, TriageResult, ImuSample } from '../api'
import { CaptureLabelKind, CaptureSource, ManualCaptureOrigin } from '../api'
import TriggerChip from './TriggerChip'
import { buildShareHash } from '../lib/captureUrl'
import { buildTimelineHash } from '../lib/deviceTimelineUrl'
import { parseWeightTrajectory } from '../lib/weightDebug'
import { groupByCategory } from '../lib/labelCategories'
import WeightTrajectoryChart from './WeightTrajectoryChart'
import PerCellReadings from './PerCellReadings'
import CalibrationBox from './CalibrationBox'

export type PanelMode = 'default' | 'length' | 'weight'

interface Props {
  /** Which body to render; the top bar is shared. The selector is shown when onModeChange is set. */
  mode?: PanelMode
  onModeChange?: (mode: PanelMode) => void
  captureItem: CaptureSummary
  detail: CaptureDetail | null
  onClose: () => void
  onTriageChange: (captureId: string, result: TriageResult) => void
  onAnnotationSave: (captureId: string, annotation: CaptureAnnotation) => void
  onMediaUploaded: (captureId: string, media: MediaSchema) => void
  onLinked: (manualCaptureId: string, deviceCaptureId: string) => void
  onLabelsChange: (captureId: string, labels: CaptureLabelSchema[]) => void
  open: boolean
  /** When true (e.g. opened from inside DeviceTimelinePage), hide the "View in timeline" link. */
  suppressTimelineLink?: boolean
  /** Open the burst view in place (DeviceTimelinePage); without it, Burst is a link to the timeline page. */
  onOpenBurst?: (captureId: string) => void
  /** Prev/next capture navigation — set by DeviceTimelinePage for in-timeline browsing. */
  onPrev?: () => void
  onNext?: () => void
  prevDisabled?: boolean
  nextDisabled?: boolean
}

interface FormValues {
  nickname: string
  species: string
  weightLbs: string
  lengthIn: string
  angler: string
  guide: string
  notes: string
  time: string
}

interface UploadEntry {
  name: string
  progress: number
  error: string | null
}

const LB_PER_GRAM = 1 / 453.592
const IN_PER_MM   = 1 / 25.4

function annField(annotation: CaptureAnnotation | null | undefined, field: string): string {
  if (!annotation) return ''
  const val = (annotation as Record<string, unknown>)[field]
  return typeof val === 'string' ? val : ''
}

function annNum(annotation: CaptureAnnotation | null | undefined, field: string): number | null {
  if (!annotation) return null
  const val = (annotation as Record<string, unknown>)[field]
  return typeof val === 'number' ? val : null
}

function toDatetimeLocal(ts: number | null | undefined): string {
  if (!ts) return ''
  const d = new Date(ts * 1000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function fmtDate(ts: number | null | undefined): string {
  if (!ts) return ''
  return new Date(ts * 1000).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  })
}

const LB_PER_GRAM_PANEL = 1 / 453.592
const IN_PER_MM_PANEL   = 1 / 25.4

function fmtWeight(grams: number | null): string {
  if (grams == null) return '—'
  return `${(grams * LB_PER_GRAM_PANEL).toFixed(2)} lb`
}

function fmtLength(mm: number | null): string {
  if (mm == null) return '—'
  return `${(mm * IN_PER_MM_PANEL).toFixed(1)} in`
}

function fmtServerLength(sl: CaptureDetail['serverLength']): string {
  if (!sl) return '—'
  if (sl.status === 'NO_FISH') return 'no fish'
  if (sl.status !== 'OK' || sl.lengthMillimeters == null) return '—'
  return fmtLength(sl.lengthMillimeters)
}

function buildFormFromAnnotation(ann: CaptureAnnotation | null): FormValues {
  const w = annNum(ann, 'measured_weight_grams')
  const l = annNum(ann, 'measured_length_mm')
  const t = annNum(ann, 'user_entered_time')
  return {
    nickname: annField(ann, 'nickname'),
    species:  annField(ann, 'species'),
    weightLbs: w != null ? (w * LB_PER_GRAM).toFixed(2) : '',
    lengthIn:  l != null ? (l * IN_PER_MM).toFixed(2) : '',
    angler:   annField(ann, 'angler'),
    guide:    annField(ann, 'guide'),
    notes:    annField(ann, 'notes'),
    time:     toDatetimeLocal(t),
  }
}

export default function CaptureSidePanel({
  mode = 'default', onModeChange, captureItem, detail, onClose, onTriageChange, onAnnotationSave, onMediaUploaded, onLinked, onLabelsChange, open,
  suppressTimelineLink, onOpenBurst, onPrev, onNext, prevDisabled, nextDisabled,
}: Props) {
  const [heroIdx, setHeroIdx] = useState(0)
  const [editingAnnotation, setEditingAnnotation] = useState(false)
  const [form, setForm] = useState<FormValues>(() => buildFormFromAnnotation(captureItem.annotation))
  const [savingAnnotation, setSavingAnnotation] = useState(false)
  const [uploads, setUploads] = useState<UploadEntry[]>([])
  const [kodiakExpanded, setKodiakExpanded] = useState(true)
  const [linkModalOpen, setLinkModalOpen] = useState(false)
  const [deviceCaptures, setDeviceCaptures] = useState<CaptureSummary[]>([])
  const [loadingDeviceCaptures, setLoadingDeviceCaptures] = useState(false)
  const [linkingId, setLinkingId] = useState<string | null>(null)
  const [linkSearch, setLinkSearch] = useState('')

  // Lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIdx, setLightboxIdx] = useState(0)
  const [lightboxList, setLightboxList] = useState<LightboxItem[]>([])
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const dragging = useRef(false)
  const dragStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 })

  const detailMedia = detail?.media ?? []
  const allImages: LightboxItem[] = [
    ...(detail?.images ?? [])
      .filter(img => img.url)
      .map(img => ({ url: img.url!, key: img.s3Key, kind: 'image' as const })),
    ...detailMedia.filter(m => m.url).map(m => ({
      url: m.url!,
      key: m.s3Key,
      kind: m.contentType?.startsWith('video/') ? ('video' as const) : ('image' as const),
    })),
  ]

  const heroItem = allImages[heroIdx] ?? null
  const heroUrl = heroItem?.url ?? null

  function openLightbox(idx: number, list: LightboxItem[] = allImages) {
    setLightboxList(list)
    setLightboxIdx(idx)
    setZoom(1)
    setPan({ x: 0, y: 0 })
    setLightboxOpen(true)
  }

  function lightboxNav(dir: 1 | -1) {
    setLightboxIdx(i => (i + dir + lightboxList.length) % lightboxList.length)
    setZoom(1)
    setPan({ x: 0, y: 0 })
  }

  function handleLightboxWheel(e: React.WheelEvent) {
    const factor = e.deltaY < 0 ? 1.15 : 0.87
    setZoom(z => {
      const next = Math.min(Math.max(z * factor, 1), 10)
      if (next <= 1) setPan({ x: 0, y: 0 })
      return next
    })
  }

  function handleLightboxMouseDown(e: React.MouseEvent) {
    if (zoom <= 1) return
    dragging.current = true
    dragStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y }
  }

  function handleLightboxMouseMove(e: React.MouseEvent) {
    if (!dragging.current) return
    setPan({
      x: dragStart.current.panX + (e.clientX - dragStart.current.x),
      y: dragStart.current.panY + (e.clientY - dragStart.current.y),
    })
  }

  function handleLightboxMouseUp() { dragging.current = false }

  useEffect(() => {
    if (!lightboxOpen) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowRight') lightboxNav(1)
      if (e.key === 'ArrowLeft')  lightboxNav(-1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxOpen, lightboxList.length])

  const annotationSource = captureItem.annotation
  const annotationCaptureId = captureItem.captureId

  useEffect(() => {
    setForm(buildFormFromAnnotation(annotationSource ?? null))
    setEditingAnnotation(false)
  }, [captureItem.captureId, annotationSource])

  useEffect(() => { setHeroIdx(0) }, [captureItem.captureId])

  async function handleAnnotationSave() {
    setSavingAnnotation(true)
    const annotation: CaptureAnnotation = {
      nickname: form.nickname || null,
      species:  form.species  || null,
      angler:   form.angler   || null,
      guide:    form.guide    || null,
      notes:    form.notes    || null,
      measured_weight_grams: form.weightLbs ? parseFloat(form.weightLbs) / LB_PER_GRAM : null,
      measured_length_mm:    form.lengthIn  ? parseFloat(form.lengthIn)  / IN_PER_MM   : null,
      user_entered_time: form.time ? new Date(form.time).getTime() / 1000 : null,
      annotated_at: Date.now() / 1000,
    } as unknown as CaptureAnnotation
    await onAnnotationSave(annotationCaptureId, annotation)
    setSavingAnnotation(false)
    setEditingAnnotation(false)
  }

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    for (const file of acceptedFiles) {
      const entry: UploadEntry = { name: file.name, progress: 0, error: null }
      setUploads(prev => [...prev, entry])
      const idx = uploads.length
      try {
        const { uploadUrl, s3Key } = await Captures.presignCaptureMedia({
          presignCaptureMediaRequest: {
            captureId: captureItem.captureId,
            filename: file.name,
            contentType: file.type || 'application/octet-stream',
          },
        })
        setUploads(prev => prev.map((u, i) => i === idx ? { ...u, progress: 30 } : u))
        await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
        })
        setUploads(prev => prev.map((u, i) => i === idx ? { ...u, progress: 80 } : u))
        const { media } = await Captures.confirmCaptureMedia({
          confirmCaptureMediaRequest: { captureId: captureItem.captureId, s3Key, contentType: file.type || 'application/octet-stream' },
        })
        setUploads(prev => prev.map((u, i) => i === idx ? { ...u, progress: 100 } : u))
        onMediaUploaded(captureItem.captureId, media)
        setTimeout(() => setUploads(prev => prev.filter((_, i) => i !== idx)), 2000)
      } catch {
        setUploads(prev => prev.map((u, i) => i === idx ? { ...u, error: 'Upload failed' } : u))
      }
    }
  }, [captureItem.captureId, uploads.length, onMediaUploaded])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [], 'video/*': [] },
  })

  async function openLinkModal() {
    setLinkModalOpen(true)
    setLinkSearch('')
    setLoadingDeviceCaptures(true)
    try {
      const hasNetRestriction = !!captureItem.hostname
      const resp = await Captures.listCaptures({
        listCapturesRequest: {
          includeAllDevices: !hasNetRestriction,
          includeDeviceIds: hasNetRestriction ? [captureItem.hostname!] : [],
          includeTriageResults: [],
        },
      })
      setDeviceCaptures((resp.captures ?? []).filter(c => c.source === CaptureSource.Device))
    } finally {
      setLoadingDeviceCaptures(false)
    }
  }

  function hasMeaningfulAnnotation(ann: CaptureAnnotation | null | undefined): boolean {
    if (!ann) return false
    const fields = ['nickname', 'species', 'angler', 'guide', 'notes', 'measured_weight_grams', 'measured_length_mm']
    return fields.some(f => {
      const v = (ann as Record<string, unknown>)[f]
      return v != null && v !== ''
    })
  }

  async function handleLink(deviceCapture: CaptureSummary) {
    if (hasMeaningfulAnnotation(deviceCapture.annotation)) {
      const nick = annField(deviceCapture.annotation, 'nickname')
      const label = nick || deviceCapture.captureId.slice(-6)
      const ok = window.confirm(
        `"${label}" already has an annotation. Linking will associate this manual entry with it. Continue?`
      )
      if (!ok) return
    }
    setLinkingId(deviceCapture.captureId)
    try {
      await Captures.linkCaptures({
        linkCapturesRequest: { manualCaptureId: captureItem.captureId, deviceCaptureId: deviceCapture.captureId },
      })
      setLinkModalOpen(false)
      onLinked(captureItem.captureId, deviceCapture.captureId)
    } finally {
      setLinkingId(null)
    }
  }

  const filteredDeviceCaptures = (() => {
    const q = linkSearch.trim().toLowerCase()
    if (!q) return []
    return deviceCaptures.filter(c => {
      if (c.captureId.toLowerCase().includes(q)) return true
      if (c.displayCode?.toLowerCase().includes(q)) return true
      const nick = annField(c.annotation, 'nickname').toLowerCase()
      if (nick && nick.includes(q)) return true
      return false
    })
  })()

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text)
  }

  const ann = annotationSource
  const hasAnnotation = ann != null
  const nickname = annField(ann, 'nickname') || null
  const isManualUnlinked = captureItem.source === CaptureSource.Manual && !captureItem.linkedCaptureId

  // "View in timeline" href — null means the button should be hidden.
  const timelineHref = (() => {
    if (suppressTimelineLink) return null
    const { source, hostname, captureId, linkedCaptureId, manualOrigin } = captureItem
    if (source === CaptureSource.Device && hostname) {
      return buildTimelineHash({ hostname, captureId })
    }
    if (source === CaptureSource.Manual && linkedCaptureId && hostname) {
      return buildTimelineHash({ hostname, captureId: linkedCaptureId })
    }
    if (source === CaptureSource.Manual && !linkedCaptureId && manualOrigin === ManualCaptureOrigin.Pi && hostname) {
      return buildTimelineHash({ hostname, captureId })
    }
    return null
  })()

  // The burst is built around a device trigger: a device capture's own, or the
  // device capture a manual one is linked to. Unlinked manual captures have none.
  const burstCaptureId =
    captureItem.source === CaptureSource.Device ? captureItem.captureId
    : captureItem.linkedCaptureId ?? null
  const burstHref = burstCaptureId && captureItem.hostname && !onOpenBurst
    ? buildTimelineHash({ hostname: captureItem.hostname, burstCaptureId })
    : null
  const burstIcon = (
    <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M2 4v8M6 4v8M10 4v8M14 4v8" />
    </svg>
  )
  const headerLinkCls = `font-mono text-[10px] tracking-[0.12em] uppercase text-stone-500 hover:text-stone-900
                  px-2 py-1 rounded-lg hover:bg-stone-100 transition-colors cursor-pointer flex items-center gap-1.5`

  return (
    <>
      {open && (
        <div
          className="md:hidden fixed inset-0 bg-black/30 z-20"
          onClick={onClose}
        />
      )}

      <Transition
        show={open}
        enter="transition ease-out duration-300"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transition ease-in duration-200"
        leaveFrom="translate-x-0"
        leaveTo="translate-x-full"
      >
        <div className={`fixed inset-y-0 right-0 w-full ${mode === 'length' ? 'md:w-[820px]' : mode === 'weight' ? 'md:w-[1100px] max-w-full' : 'md:w-[560px]'} bg-white border-l border-stone-200 shadow-xl z-30 flex flex-col overflow-hidden`}>

          {/* Top bar */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200 shrink-0 gap-2">
            <div className="flex items-center gap-1">
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-500 hover:text-stone-900 transition-colors cursor-pointer"
                aria-label="Close"
              >
                <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <path d="M4 4l8 8 M12 4l-8 8" />
                </svg>
              </button>
              {onPrev !== undefined && (
                <button
                  onClick={onPrev}
                  disabled={prevDisabled}
                  className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-500 hover:text-stone-900 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-default"
                  aria-label="Previous capture"
                >
                  <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M10 4l-4 4 4 4" />
                  </svg>
                </button>
              )}
              {onNext !== undefined && (
                <button
                  onClick={onNext}
                  disabled={nextDisabled}
                  className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-500 hover:text-stone-900 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-default"
                  aria-label="Next capture"
                >
                  <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M6 4l4 4-4 4" />
                  </svg>
                </button>
              )}
            </div>
            <div className="flex items-center gap-1">
              {onModeChange && (
                <select
                  value={mode}
                  onChange={e => onModeChange(e.target.value as PanelMode)}
                  aria-label="Panel view"
                  className="mr-1 font-mono text-[10px] tracking-[0.12em] uppercase text-stone-600 border border-stone-200
                    px-2 py-1 rounded-lg hover:border-stone-400 transition-colors cursor-pointer bg-white"
                >
                  <option value="default">Default</option>
                  <option value="length">Length debug</option>
                  <option value="weight">Weight debug</option>
                </select>
              )}
              {timelineHref && (
                <a
                  href={timelineHref}
                  className="font-mono text-[10px] tracking-[0.12em] uppercase text-stone-500 hover:text-stone-900
                    px-2 py-1 rounded-lg hover:bg-stone-100 transition-colors cursor-pointer flex items-center gap-1.5"
                  title="View in timeline"
                >
                  <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M2 8h12M8 3l5 5-5 5" strokeLinejoin="round"/>
                  </svg>
                  Timeline
                </a>
              )}
              {burstCaptureId && onOpenBurst && (
                <button onClick={() => onOpenBurst(burstCaptureId)} className={headerLinkCls}
                  title="Every trigger within 3 min of this one: frames and weight on one strip">
                  {burstIcon}
                  Burst
                </button>
              )}
              {burstHref && (
                <a href={burstHref} className={headerLinkCls}
                  title="Every trigger within 3 min of this one: frames and weight on one strip">
                  {burstIcon}
                  Burst
                </a>
              )}
              <button
                onClick={() => copyToClipboard(`${window.location.origin}/${buildShareHash(captureItem)}`)}
                className="font-mono text-[10px] tracking-[0.12em] uppercase text-stone-500 hover:text-stone-900
                  px-2 py-1 rounded-lg hover:bg-stone-100 transition-colors cursor-pointer flex items-center gap-1.5"
              >
                <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <rect x="5" y="5" width="8" height="8" rx="1" />
                  <path d="M11 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v7a1 1 0 001 1h2" />
                </svg>
                Copy link
              </button>
            </div>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto">
          {mode !== 'default' ? (
            <>
              <div className="px-4 py-3 border-b border-stone-100">
                <p className="font-mono text-xs text-stone-500 tracking-wide flex items-center gap-2 flex-wrap">
                  <span>{nickname ? `${nickname} · ` : ''}{captureItem.hostname ?? 'Manual'} · {fmtDate(captureItem.captureStartTimestamp)} · <span className="text-stone-400">{captureItem.captureId.slice(-6)}</span></span>
                  <TriggerChip source={captureItem.triggerSource} />
                </p>
              </div>
              <LabelToggles
                kind={mode === 'length' ? CaptureLabelKind.Length : CaptureLabelKind.Weight}
                captureId={captureItem.captureId}
                applied={captureItem.labels ?? []}
                onChange={onLabelsChange}
              />
              {detail === null ? (
                <p className="px-4 py-6 font-mono text-[10px] tracking-[0.2em] uppercase text-stone-400 animate-pulse">Loading…</p>
              ) : mode === 'length' ? (
                <LengthDebugBody detail={detail} onOpen={openLightbox} />
              ) : (
                <WeightDebugBody detail={detail} />
              )}
            </>
          ) : (<>

            {/* Image carousel */}
            <div className="bg-stone-900">
              <div
                className="relative h-56 flex items-center justify-center group overflow-hidden"
                onClick={() => heroUrl && heroItem?.kind === 'image' && openLightbox(heroIdx)}
              >
                {/* Blurred hero image backdrop — water-light haze effect */}
                {heroUrl && (
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      backgroundImage: `url(${heroUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      filter: 'blur(20px) saturate(0.8) brightness(0.55)',
                      transform: 'scale(1.15)',
                    }}
                  />
                )}
                {detail === null ? (
                  <div className="flex items-center justify-center h-full w-full">
                    <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-stone-400 animate-pulse">Loading…</p>
                  </div>
                ) : heroUrl && heroItem?.kind === 'video' ? (
                  <video
                    key={heroItem.key}
                    src={heroUrl}
                    controls
                    preload="metadata"
                    className="relative max-h-56 w-full object-contain bg-black"
                  />
                ) : heroUrl ? (
                  <>
                    <img src={heroUrl} alt="" className="relative max-h-56 w-full object-contain cursor-zoom-in" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                      <div className="bg-black/50 rounded-full p-2">
                        <svg viewBox="0 0 20 20" className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <circle cx="8" cy="8" r="5" />
                          <path d="M12 12l4 4" strokeLinecap="round" />
                          <path d="M8 6v4M6 8h4" strokeLinecap="round" />
                        </svg>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-full w-full">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-stone-600">No images</span>
                  </div>
                )}
              </div>
              {allImages.length > 1 && (
                <div className="flex gap-1.5 overflow-x-auto px-3 pb-3 pt-1.5">
                  {allImages.map((img, i) => (
                    <button
                      key={img.key}
                      onClick={() => setHeroIdx(i)}
                      className={`relative shrink-0 w-12 h-12 rounded overflow-hidden border-2 transition-colors cursor-pointer
                        ${i === heroIdx ? 'border-bear' : 'border-transparent hover:border-stone-500'}`}
                    >
                      {img.kind === 'video' ? (
                        <>
                          <video src={img.url} preload="metadata" muted className="w-full h-full object-cover bg-black" />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
                            <svg viewBox="0 0 20 20" className="w-5 h-5 text-white drop-shadow" fill="currentColor">
                              <path d="M6 4l10 6-10 6V4z" />
                            </svg>
                          </div>
                        </>
                      ) : (
                        <img src={img.url} alt="" className="w-full h-full object-cover" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Capture title */}
            <div className="px-4 py-3 border-b border-stone-100">
              {nickname && (
                <h2 className="font-display font-bold text-xl text-stone-900">{nickname}</h2>
              )}
              <p className="font-mono text-xs text-stone-500 tracking-wide flex items-center gap-2 flex-wrap">
                <span>{captureItem.hostname ?? 'Manual'} · {fmtDate(captureItem.captureStartTimestamp)} · <span className="text-stone-400">{captureItem.captureId.slice(-6)}</span></span>
                <TriggerChip source={captureItem.triggerSource} />
              </p>
            </div>

            {/* Triage — current state highlighted, click any option to change */}
            <div className="px-4 py-3 border-b border-stone-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1 bg-stone-200" />
                <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-stone-600">Triage</span>
                <div className="h-px flex-1 bg-stone-200" />
              </div>
              <div className="flex gap-2">
                {([
                  { value: 'keep' as TriageResult,    label: 'Keep',      active: 'bg-pine text-white font-semibold',           idle: 'bg-pine/10 text-pine hover:bg-pine/20' },
                  { value: 'pending' as TriageResult, label: 'Untriaged', active: 'bg-amber-400 text-amber-900 font-semibold',   idle: 'bg-amber-50 text-amber-700 hover:bg-amber-100' },
                  { value: 'discard' as TriageResult, label: 'Discard',   active: 'bg-coral text-white font-semibold',          idle: 'bg-coral/10 text-coral hover:bg-coral/20' },
                ]).map(opt => {
                  const isCurrent = captureItem.triageResult === opt.value
                  return (
                    <button
                      key={opt.value}
                      onClick={() => !isCurrent && onTriageChange(captureItem.captureId, opt.value)}
                      disabled={isCurrent}
                      className={`flex-1 py-2.5 rounded-xl font-mono text-[11px] tracking-[0.15em] uppercase transition-colors ${isCurrent ? `${opt.active} cursor-default` : `${opt.idle} cursor-pointer`}`}
                    >
                      {opt.label}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Catch labels: about the catch as a whole (length and weight
                labels live in their debug views) */}
            <LabelToggles
              kind={CaptureLabelKind.Catch}
              captureId={captureItem.captureId}
              applied={captureItem.labels ?? []}
              onChange={onLabelsChange}
            />

            {/* Link to device capture — manual only */}
            {isManualUnlinked && (
              <div className="px-4 py-3 border-b border-stone-100">
                <button
                  onClick={openLinkModal}
                  className="w-full py-2.5 rounded-xl border border-stone-200 hover:border-stone-300 hover:bg-stone-50
                    font-mono text-[11px] tracking-[0.15em] uppercase text-stone-600 hover:text-stone-900
                    transition-colors cursor-pointer"
                >
                  Link to device capture
                </button>
              </div>
            )}

            {/* Annotation section */}
            <div className="px-4 py-3 border-b border-stone-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1 bg-stone-200" />
                <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-stone-600">Annotation</span>
                <div className="h-px flex-1 bg-stone-200" />
                {hasAnnotation && !editingAnnotation && (
                  <button
                    onClick={() => setEditingAnnotation(true)}
                    className="font-mono text-[10px] tracking-[0.12em] uppercase text-stone-600 hover:text-stone-900
                      px-3 py-1.5 rounded-lg border border-stone-200 hover:bg-stone-50 transition-colors cursor-pointer"
                  >
                    Edit
                  </button>
                )}
              </div>

              {!hasAnnotation && !editingAnnotation ? (
                <button
                  onClick={() => setEditingAnnotation(true)}
                  className="w-full py-3 rounded-xl border-2 border-dashed border-stone-200 hover:border-bear/40
                    font-mono text-[11px] tracking-[0.15em] uppercase text-stone-400 hover:text-bear
                    transition-colors cursor-pointer"
                >
                  + Add annotation
                </button>
              ) : editingAnnotation ? (
                <div className="space-y-3">
                  <FormField label="Nickname">
                    <input
                      type="text"
                      value={form.nickname}
                      onChange={e => setForm(f => ({ ...f, nickname: e.target.value }))}
                      className="form-input-cls"
                      placeholder="e.g. Big Bob"
                    />
                  </FormField>
                  <FormField label="Species">
                    <input
                      type="text"
                      value={form.species}
                      onChange={e => setForm(f => ({ ...f, species: e.target.value }))}
                      className="form-input-cls"
                      placeholder="Chinook"
                    />
                  </FormField>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Weight (lbs)">
                      <input
                        type="number"
                        step="0.01"
                        value={form.weightLbs}
                        onChange={e => setForm(f => ({ ...f, weightLbs: e.target.value }))}
                        className="form-input-cls"
                        placeholder="0.00"
                      />
                    </FormField>
                    <FormField label="Length (in)">
                      <input
                        type="number"
                        step="0.1"
                        value={form.lengthIn}
                        onChange={e => setForm(f => ({ ...f, lengthIn: e.target.value }))}
                        className="form-input-cls"
                        placeholder="0.0"
                      />
                    </FormField>
                  </div>
                  <FormField label="Angler">
                    <input
                      type="text"
                      value={form.angler}
                      onChange={e => setForm(f => ({ ...f, angler: e.target.value }))}
                      className="form-input-cls"
                    />
                  </FormField>
                  <FormField label="Guide">
                    <input
                      type="text"
                      value={form.guide}
                      onChange={e => setForm(f => ({ ...f, guide: e.target.value }))}
                      className="form-input-cls"
                    />
                  </FormField>
                  <FormField label="Notes">
                    <textarea
                      value={form.notes}
                      onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                      className="form-input-cls min-h-[72px] resize-none"
                      rows={3}
                    />
                  </FormField>
                  <FormField label="Time">
                    <input
                      type="datetime-local"
                      value={form.time}
                      onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
                      className="form-input-cls"
                    />
                  </FormField>
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={handleAnnotationSave}
                      disabled={savingAnnotation}
                      className="flex-1 py-2 rounded-lg bg-bear text-white font-mono text-[11px] tracking-[0.15em] uppercase
                        hover:bg-bear-light transition-colors cursor-pointer disabled:opacity-50"
                    >
                      {savingAnnotation ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setForm(buildFormFromAnnotation(annotationSource ?? null))
                        setEditingAnnotation(false)
                      }}
                      className="px-4 py-2 rounded-lg border border-stone-200 text-stone-600 font-mono text-[11px] tracking-[0.15em] uppercase
                        hover:bg-stone-50 transition-colors cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {nickname && <ReadField label="Nickname" value={nickname} />}
                  {annField(ann, 'species') && <ReadField label="Species" value={annField(ann, 'species')} />}
                  {annNum(ann, 'measured_weight_grams') != null && (
                    <ReadField label="Weight" value={`${(annNum(ann, 'measured_weight_grams')! * LB_PER_GRAM).toFixed(2)} lbs`} />
                  )}
                  {annNum(ann, 'measured_length_mm') != null && (
                    <ReadField label="Length" value={`${(annNum(ann, 'measured_length_mm')! * IN_PER_MM).toFixed(1)} in`} />
                  )}
                  {annField(ann, 'angler') && <ReadField label="Angler" value={annField(ann, 'angler')} />}
                  {annField(ann, 'guide') && <ReadField label="Guide" value={annField(ann, 'guide')} />}
                  {annField(ann, 'notes') && <ReadField label="Notes" value={annField(ann, 'notes')} />}
                  {annNum(ann, 'user_entered_time') != null && (
                    <ReadField label="Time" value={fmtDate(annNum(ann, 'user_entered_time'))} />
                  )}
                </div>
              )}
            </div>

            {/* Kodiak Data section */}
            {detail && (
              <div className="px-4 py-3 border-b border-stone-100">
                <button
                  onClick={() => setKodiakExpanded(e => !e)}
                  className="flex items-center gap-3 w-full cursor-pointer group"
                >
                  <div className="h-px flex-1 bg-stone-200" />
                  <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-stone-600">Kodiak Data</span>
                  <div className="h-px flex-1 bg-stone-200" />
                  <svg
                    viewBox="0 0 16 16"
                    className={`w-3.5 h-3.5 text-stone-400 transition-transform shrink-0 ${kodiakExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  >
                    <path d="M4 6l4 4 4-4" />
                  </svg>
                </button>

                {kodiakExpanded && (
                  <div className="mt-3 space-y-3">
                    {/* IDs */}
                    <div className="space-y-1.5">
                      <CopyField label="Capture ID" value={detail.captureId} onCopy={() => copyToClipboard(detail.captureId)} />
                      {detail.catchId && (
                        <CopyField label="Catch ID" value={detail.catchId} onCopy={() => copyToClipboard(detail.catchId!)} />
                      )}
                      {detail.runId && (
                        <CopyField label="Run ID" value={detail.runId} onCopy={() => copyToClipboard(detail.runId!)} />
                      )}
                    </div>

                    {/* Single-capture sensor data */}
                    <div className="rounded-lg bg-stone-50 border border-stone-100 px-3 py-2.5 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-stone-500">
                          Sensor data
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        <ReadField label="Weight" value={fmtWeight(detail.weightGrams)} />
                        <ReadField label="Length" value={fmtLength(detail.lengthMillimeters)} />
                        {detail.lengthMethod && (
                          <ReadField
                            label="Length Method"
                            value={detail.lengthConfidence != null
                              ? `${detail.lengthMethod} · ${detail.lengthConfidence.toFixed(2)}`
                              : detail.lengthMethod}
                          />
                        )}
                        {detail.serverLength && (
                          <ReadField label="Server Length" value={fmtServerLength(detail.serverLength)} />
                        )}
                        {detail.gpsLastValidFix && (
                          <>
                            <ReadField label="Lat" value={detail.gpsLastValidFix.lat.toFixed(5)} />
                            <ReadField label="Lon" value={detail.gpsLastValidFix.lon.toFixed(5)} />
                          </>
                        )}
                      </div>
                      {/* Server species (Fishial): top-3 candidates with confidences.
                          Always shown with their numbers — the scores convey uncertainty. */}
                      {detail.speciesResult && (
                        <div className="pt-1">
                          <span className="font-mono text-[9px] tracking-[0.15em] uppercase text-stone-500">Server Species</span>
                          {detail.speciesResult.predictions.length > 0 ? (
                            <div className="mt-0.5 space-y-0.5">
                              {detail.speciesResult.predictions.slice(0, 3).map(p => (
                                <div key={p.name} className="flex justify-between items-baseline gap-4">
                                  <span className="font-mono text-xs text-stone-700">
                                    {commonName(p.name)
                                      ? <>{commonName(p.name)} <span className="italic text-stone-400">· {p.name}</span></>
                                      : <span className="italic">{p.name}</span>}
                                  </span>
                                  <span className="font-mono text-xs text-stone-500">{Math.round(p.score * 100)}%</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="font-mono text-xs text-stone-400 mt-0.5">no fish detected</p>
                          )}
                        </div>
                      )}
                      {(detail.hasError || detail.imageError || detail.lengthError) && (
                        <div className="space-y-0.5 pt-0.5">
                          {detail.imageError && (
                            <p className="font-mono text-[9px] text-coral">img: {detail.imageError}</p>
                          )}
                          {detail.lengthError && (
                            <p className="font-mono text-[9px] text-coral">len: {detail.lengthError}</p>
                          )}
                          {detail.hasError && !detail.imageError && !detail.lengthError && (
                            <p className="font-mono text-[9px] text-coral">error</p>
                          )}
                        </div>
                      )}
                    </div>
                    <WeightDebugCharts detail={detail} />
                    <ServerLengthDebug detail={detail} />
                    {detail.imu && <ImuReadout imu={detail.imu} />}
                    {detail.resultJsonPresignedUrl && (
                      <a
                        className="block font-mono text-[9px] text-stone-400 hover:text-stone-600 underline"
                        href={detail.resultJsonPresignedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        view raw result.json →
                      </a>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Media upload zone */}
            <div className="px-4 py-3">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1 bg-stone-200" />
                <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-stone-600">Upload media</span>
                <div className="h-px flex-1 bg-stone-200" />
              </div>
              <div
                {...getRootProps()}
                className={`rounded-xl border-2 border-dashed px-4 py-5 text-center cursor-pointer transition-colors
                  ${isDragActive
                    ? 'border-bear/60 bg-bear/5'
                    : 'border-stone-200 hover:border-stone-300 hover:bg-stone-50'
                  }`}
              >
                <input {...getInputProps()} />
                <p className="font-mono text-[11px] text-stone-400">
                  {isDragActive ? 'Drop to upload' : 'Drag & drop or click to select images/video'}
                </p>
              </div>
              {uploads.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {uploads.map((u, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-mono text-[10px] text-stone-700 truncate">{u.name}</p>
                        {u.error ? (
                          <p className="font-mono text-[10px] text-coral">{u.error}</p>
                        ) : (
                          <div className="h-1 bg-stone-100 rounded-full mt-1">
                            <div
                              className="h-1 bg-pine rounded-full transition-all duration-300"
                              style={{ width: `${u.progress}%` }}
                            />
                          </div>
                        )}
                      </div>
                      <span className="font-mono text-[10px] text-stone-400 shrink-0">
                        {u.error ? '✗' : u.progress === 100 ? '✓' : `${u.progress}%`}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>)}
          </div>
        </div>
      </Transition>

      {/* Link to device capture modal */}
      <Dialog open={linkModalOpen} onClose={() => setLinkModalOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-stone-100">
              <Dialog.Title className="font-display font-bold text-stone-900">Link to device capture</Dialog.Title>
              <p className="font-mono text-[11px] text-stone-400 mt-0.5">Select the device capture this manual entry corresponds to</p>
              {captureItem.hostname && (
                <p className="font-mono text-[10px] text-stone-500 mt-2">
                  Searching in net: <span className="text-stone-900">{captureItem.hostname}</span>
                </p>
              )}
            </div>
            <div className="px-5 py-3 border-b border-stone-100">
              <input
                type="text"
                autoFocus
                value={linkSearch}
                onChange={e => setLinkSearch(e.target.value)}
                placeholder="Search by code, nickname, or capture ID…"
                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg
                  focus:outline-none focus:ring-2 focus:ring-bear/30 focus:border-bear/50"
              />
            </div>
            <div className="max-h-96 overflow-y-auto">
              {loadingDeviceCaptures ? (
                <div className="flex items-center justify-center py-10">
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-stone-400 animate-pulse">Loading…</p>
                </div>
              ) : !linkSearch.trim() ? (
                <div className="flex items-center justify-center py-10 px-5">
                  <p className="font-mono text-[11px] text-stone-400 text-center">
                    Enter a search term to find matching device captures
                    {deviceCaptures.length > 0 && <> ({deviceCaptures.length} available)</>}
                  </p>
                </div>
              ) : filteredDeviceCaptures.length === 0 ? (
                <div className="flex items-center justify-center py-10">
                  <p className="font-mono text-[11px] text-stone-400">No matches</p>
                </div>
              ) : (
                <div className="divide-y divide-stone-100">
                  {filteredDeviceCaptures.map(c => {
                    const annotated = hasMeaningfulAnnotation(c.annotation)
                    return (
                      <button
                        key={c.captureId}
                        disabled={linkingId !== null}
                        onClick={() => handleLink(c)}
                        className="w-full text-left px-5 py-3 hover:bg-stone-50 transition-colors cursor-pointer disabled:opacity-50"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 shrink-0 rounded overflow-hidden bg-stone-100 flex items-center justify-center">
                            {c.thumbnailUrl ? (
                              <img src={c.thumbnailUrl} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                            ) : (
                              <span className="font-mono text-[8px] text-stone-400">—</span>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-mono text-xs text-stone-900 truncate">
                                {annField(c.annotation, 'nickname') || c.hostname || '—'}
                              </p>
                              {annotated && (
                                <span className="font-mono text-[9px] tracking-[0.15em] uppercase text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded shrink-0">
                                  Annotated
                                </span>
                              )}
                            </div>
                            <p className="font-mono text-[10px] text-stone-400 mt-0.5 truncate">
                              {fmtDate(c.captureStartTimestamp)} · {c.captureId.slice(-6)}
                            </p>
                          </div>
                          {linkingId === c.captureId && (
                            <p className="font-mono text-[10px] text-stone-400 shrink-0 animate-pulse">Linking…</p>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-stone-100 flex justify-end">
              <button
                onClick={() => setLinkModalOpen(false)}
                className="font-mono text-[11px] tracking-[0.12em] uppercase text-stone-500 hover:text-stone-900
                  px-4 py-2 rounded-lg border border-stone-200 hover:bg-stone-50 transition-colors cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onClose={() => setLightboxOpen(false)} className="relative z-[70]">
        <div className="fixed inset-0 bg-black/95" />
        <div className="fixed inset-0 flex flex-col select-none">

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 shrink-0 text-white/80">
            <button
              onClick={() => setLightboxOpen(false)}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
            >
              <svg viewBox="0 0 20 20" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M5 5l10 10M15 5L5 15" />
              </svg>
            </button>
            <span className="font-mono text-[11px] tracking-widest text-white/50">
              {lightboxIdx + 1} / {lightboxList.length}
            </span>
            {/* Zoom controls */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => setZoom(z => Math.max(z * 0.75, 1))}
                className="px-2 py-1.5 rounded-lg hover:bg-white/10 font-mono text-sm text-white/70 hover:text-white transition-colors cursor-pointer"
              >−</button>
              <span className="font-mono text-[10px] text-white/40 w-10 text-center tabular-nums">
                {Math.round(zoom * 100)}%
              </span>
              <button
                onClick={() => setZoom(z => Math.min(z * 1.33, 10))}
                className="px-2 py-1.5 rounded-lg hover:bg-white/10 font-mono text-sm text-white/70 hover:text-white transition-colors cursor-pointer"
              >+</button>
            </div>
          </div>

          {/* Image area */}
          <div
            className="flex-1 overflow-hidden flex items-center justify-center relative"
            onWheel={handleLightboxWheel}
            onMouseDown={handleLightboxMouseDown}
            onMouseMove={handleLightboxMouseMove}
            onMouseUp={handleLightboxMouseUp}
            onMouseLeave={handleLightboxMouseUp}
            style={{ cursor: zoom > 1 ? 'grab' : 'default' }}
          >
            {lightboxList[lightboxIdx] && lightboxList[lightboxIdx].kind === 'video' ? (
              <video
                src={lightboxList[lightboxIdx].url}
                controls
                autoPlay
                className="max-w-full max-h-full object-contain"
              />
            ) : lightboxList[lightboxIdx] && (
              <img
                src={lightboxList[lightboxIdx].url}
                alt=""
                draggable={false}
                className="max-w-full max-h-full object-contain pointer-events-none"
                style={{
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  transformOrigin: 'center',
                  transition: dragging.current ? 'none' : 'transform 0.1s ease',
                }}
              />
            )}

            {/* Prev / Next */}
            {lightboxList.length > 1 && (
              <>
                <button
                  onClick={e => { e.stopPropagation(); lightboxNav(-1) }}
                  className="absolute left-3 p-2 rounded-full bg-black/40 hover:bg-black/70 text-white transition-colors cursor-pointer"
                >
                  <svg viewBox="0 0 20 20" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M12 4l-6 6 6 6" />
                  </svg>
                </button>
                <button
                  onClick={e => { e.stopPropagation(); lightboxNav(1) }}
                  className="absolute right-3 p-2 rounded-full bg-black/40 hover:bg-black/70 text-white transition-colors cursor-pointer"
                >
                  <svg viewBox="0 0 20 20" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M8 4l6 6-6 6" />
                  </svg>
                </button>
              </>
            )}
          </div>

          {/* Thumbnail strip */}
          {lightboxList.length > 1 && (
            <div className="flex gap-2 justify-center px-4 py-3 overflow-x-auto shrink-0">
              {lightboxList.map((img, i) => (
                <button
                  key={img.key}
                  onClick={() => { setLightboxIdx(i); setZoom(1); setPan({ x: 0, y: 0 }) }}
                  className={`shrink-0 w-14 h-14 rounded overflow-hidden transition-all cursor-pointer
                    ${i === lightboxIdx ? 'ring-2 ring-white opacity-100' : 'opacity-40 hover:opacity-70'}`}
                >
                  <img src={img.url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      </Dialog>
    </>
  )
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block font-mono text-[9px] tracking-[0.2em] uppercase text-stone-500 mb-1">
        {label}
      </label>
      {children}
    </div>
  )
}

function ReadField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-baseline gap-4">
      <span className="font-mono text-[9px] tracking-[0.15em] uppercase text-stone-500 shrink-0">{label}</span>
      <span className="font-mono text-xs text-stone-700 text-right">{value}</span>
    </div>
  )
}

function CopyField({ label, value, onCopy }: { label: string; value: string; onCopy: () => void }) {
  const [copied, setCopied] = useState(false)
  function handleCopy() {
    onCopy()
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="font-mono text-[9px] tracking-[0.15em] uppercase text-stone-500 shrink-0">{label}</span>
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="font-mono text-[10px] text-stone-600 truncate">{value.slice(-12)}</span>
        <button
          onClick={handleCopy}
          className="shrink-0 p-1 rounded hover:bg-stone-100 transition-colors cursor-pointer"
          title="Copy"
        >
          {copied ? (
            <svg viewBox="0 0 16 16" className="w-3 h-3 text-pine" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M3 8l3.5 3.5L13 5" />
            </svg>
          ) : (
            <svg viewBox="0 0 16 16" className="w-3 h-3 text-stone-400" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <rect x="5" y="5" width="8" height="8" rx="1" />
              <path d="M11 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v7a1 1 0 001 1h2" />
            </svg>
          )}
        </button>
      </div>
    </div>
  )
}


function ImuReadout({ imu }: { imu: ImuSample }) {
  const n = (v: unknown): number | null => (typeof v === 'number' ? v : null)
  const roll = n(imu.rollDegrees)
  const pitch = n(imu.pitchDegrees)
  const ax = n(imu.accGX)
  const ay = n(imu.accGY)
  const az = n(imu.accGZ)
  const fmt = (v: number | null, suffix: string) => (v == null ? '—' : `${v.toFixed(2)}${suffix}`)
  return (
    <div className="rounded-lg bg-stone-50 border border-stone-100 px-3 py-2.5 space-y-1.5">
      <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-stone-500">IMU (median)</span>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <ReadField label="Roll" value={fmt(roll, '°')} />
        <ReadField label="Pitch" value={fmt(pitch, '°')} />
        <ReadField label="Accel X" value={fmt(ax, ' g')} />
        <ReadField label="Accel Y" value={fmt(ay, ' g')} />
        <ReadField label="Accel Z" value={fmt(az, ' g')} />
      </div>
    </div>
  )
}

function ServerLengthDebug({ detail }: { detail: CaptureDetail }) {
  const sl = detail.serverLength
  if (!sl) return null
  // The device's on-net overlay is already in images (length/annotated.png);
  // the server overlay is the presigned maskUrl in kodiak-data.
  const deviceOverlay =
    (detail.images ?? []).find(
      i => i.s3Key.endsWith('length/annotated.png') && i.url,
    )?.url ?? null
  const ok = sl.status === 'OK'
  return (
    <div className="rounded-lg bg-stone-50 border border-stone-100 px-3 py-2.5 space-y-1.5">
      <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-stone-500">
        Length debug (on-net vs server)
      </span>
      <div className="grid grid-cols-2 gap-2 pt-0.5">
        <LengthDebugPane
          title="On-net"
          imgUrl={deviceOverlay}
          label={fmtLength(detail.lengthMillimeters)}
        />
        <LengthDebugPane
          title="Server (Fishial)"
          imgUrl={ok ? sl.maskUrl : null}
          label={
            ok
              ? fmtLength(sl.lengthMillimeters)
              : sl.status === 'NO_FISH'
                ? 'no fish'
                : 'error'
          }
          sub={
            ok && sl.confidence != null
              ? `conf ${sl.confidence.toFixed(2)} · frame ${sl.chosenFrameIndex ?? '—'}`
              : undefined
          }
        />
      </div>
    </div>
  )
}

function LengthDebugPane({
  title,
  imgUrl,
  label,
  sub,
}: {
  title: string
  imgUrl: string | null
  label: string
  sub?: string
}) {
  return (
    <div className="space-y-1">
      <div className="font-mono text-[9px] tracking-[0.15em] uppercase text-stone-400">
        {title}
      </div>
      {imgUrl ? (
        <img
          src={imgUrl}
          alt={`${title} mask`}
          className="w-full rounded border border-stone-200 object-contain bg-stone-100"
        />
      ) : (
        <div className="w-full aspect-[4/3] rounded border border-dashed border-stone-200 flex items-center justify-center font-mono text-[9px] text-stone-400">
          no image
        </div>
      )}
      <div className="font-mono text-[10px] text-stone-700">
        {label}
        {sub ? <span className="text-stone-400"> · {sub}</span> : null}
      </div>
    </div>
  )
}

function LoadCellHealth({
  observed,
  expected,
}: {
  observed: Record<string, number>
  expected: Record<string, number> | null
}) {
  const cells = Object.keys(observed).sort((a, b) => a.localeCompare(b))
  return (
    <div className="rounded-lg bg-stone-50 border border-stone-100 px-3 py-2.5 space-y-1.5">
      <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-stone-500">
        Load cell health
      </span>
      <div className="space-y-1 pt-0.5">
        {cells.map(ch => (
          <CellFractionBar
            key={ch}
            cell={ch}
            observed={observed[ch]}
            expected={expected ? (expected[ch] ?? null) : null}
          />
        ))}
      </div>
    </div>
  )
}

function CellFractionBar({
  cell,
  observed,
  expected,
}: {
  cell: string
  observed: number
  expected: number | null
}) {
  const observedPct = Math.max(0, Math.min(1, observed)) * 100
  const expectedPct = expected != null ? Math.max(0, Math.min(1, expected)) * 100 : null
  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-[9px] text-stone-500 w-4 shrink-0">{cell}</span>
      <div className="relative flex-1 h-2 rounded bg-stone-200 overflow-visible">
        <div
          className="absolute inset-y-0 left-0 bg-coral rounded"
          style={{ width: `${observedPct}%` }}
        />
        {expectedPct != null && (
          <div
            className="absolute -top-0.5 -bottom-0.5 w-px bg-stone-700"
            style={{ left: `${expectedPct}%` }}
            title={`expected ${expectedPct.toFixed(1)}%`}
          />
        )}
      </div>
      <span className="font-mono text-[9px] text-stone-500 w-9 text-right shrink-0">
        {observedPct.toFixed(1)}%
      </span>
    </div>
  )
}

interface LightboxItem { url: string; key: string; kind: 'image' | 'video' }

// Server's midline gate (backend app/length/midline_onnx.py CONF_THRESHOLD).
const MIDLINE_CONF_THRESHOLD = 0.25

export function pctErr(mm: number, ref: number): string {
  const pct = ((mm - ref) / ref) * 100
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(0)}%`
}

/** Yes/no labels of one kind (defined by admins on the Settings page), one row
 *  per category, switched on or off for this capture with a click. */
function LabelToggles({ kind, captureId, applied, onChange }: {
  kind: CaptureLabelKind
  captureId: string
  applied: CaptureLabelSchema[]
  onChange: (captureId: string, labels: CaptureLabelSchema[]) => void
}) {
  const [labels, setLabels] = useState<CaptureLabelSchema[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    Captures.listCaptureLabels({ body: {} })
      .then(r => setLabels(r.labels))
      .catch(e => setError(String(e)))
  }, [])

  async function toggle(label: CaptureLabelSchema) {
    const on = !applied.some(a => a.id === label.id)
    const prev = applied
    onChange(captureId, on ? [...applied, label] : applied.filter(a => a.id !== label.id))
    setError(null)
    try {
      const r = await Captures.setCaptureLabel({
        setCaptureLabelRequest: { captureIds: [captureId], labelId: label.id, applied: on },
      })
      onChange(captureId, r.captures[0].labels ?? [])
    } catch (e) {
      onChange(captureId, prev)
      setError(String(e))
    }
  }

  const ofKind = (labels ?? []).filter(l => l.kind === kind)
  return (
    <div className="px-4 py-3 border-b border-stone-100">
      <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-stone-400 mb-2">{kind} labels</p>
      {error && <p className="text-xs text-rose-700 mb-2">{error}</p>}
      {labels === null ? (
        <p className="font-mono text-[10px] text-stone-400 animate-pulse">Loading…</p>
      ) : ofKind.length === 0 ? (
        <p className="text-xs text-stone-500">No {kind} labels yet. Admins can add them under Settings.</p>
      ) : (
        <div className="space-y-2">
          {groupByCategory(ofKind).map(([category, group]) => (
            <div key={category}>
              {category && <p className="font-mono text-[9px] tracking-[0.15em] uppercase text-stone-500 mb-1">{category}</p>}
              <div className="flex flex-wrap gap-1.5">
                {group.map(label => {
                  const on = applied.some(a => a.id === label.id)
                  return (
                    <button key={label.id} type="button" onClick={() => toggle(label)}
                      title={label.description ?? undefined}
                      aria-pressed={on}
                      className={`font-mono text-[10px] tracking-wide px-2 py-1 rounded border transition-colors cursor-pointer ${
                        on ? 'bg-coral/10 text-coral border-coral/40' : 'bg-white text-stone-500 border-stone-200 hover:border-stone-400'
                      }`}>
                      {on ? '✓ ' : ''}{label.name}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function DebugBox({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg bg-stone-50 border border-stone-100 px-3 py-2.5 space-y-1.5">
      <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-stone-500">{title}</span>
      {children}
    </div>
  )
}

function WeightDebugCharts({ detail }: { detail: CaptureDetail }) {
  const traj = parseWeightTrajectory(detail.weightDebug)
  const asNum = (v: unknown): number | null => (typeof v === 'number' ? v : null)
  const chosen = asNum(detail.chosenFrameIndex)
  const frames = (detail.frames ?? []).map(f => ({
    index: f.frameIndex,
    t: f.captureTimestamp,
    chosen: f.frameIndex === chosen,
  }))
  return (
    <>
      {detail.weightDebug?.observedCellFractions && (
        <LoadCellHealth
          observed={detail.weightDebug.observedCellFractions as unknown as Record<string, number>}
          expected={(detail.weightDebug.calibration?.expectedFractions as unknown as Record<string, number> | undefined) ?? null}
        />
      )}
      {traj && (
        <>
          <WeightTrajectoryChart
            trajectory={traj}
            captureStart={asNum(detail.captureStartTimestamp)}
            captureEnd={asNum(detail.captureEndTimestamp)}
            frames={frames}
            hostname={detail.hostname}
            captureId={detail.captureId}
          />
          <PerCellReadings trajectory={traj} />
          <CalibrationBox cells={traj.cellCalibration} />
        </>
      )}
    </>
  )
}

// Per-guide estimates kept as an extra annotation key by sheet imports (e.g.
// NASS26: {"Steve": {"weight_lb": "20", "length_in": "40"}, ...}); measured_* is
// their mean. Free text, so a value may be a range like "18-20" or "?".
function guideWeightEstimates(annotation: CaptureAnnotation | null | undefined): string | null {
  const est = (annotation as Record<string, unknown> | null | undefined)?.['guide_estimates']
  if (!est || typeof est !== 'object') return null
  const parts = Object.entries(est as Record<string, { weight_lb?: unknown }>)
    .map(([guide, e]) => {
      const w = e?.weight_lb
      return w != null && w !== '' ? `${guide} ${String(w)} lb` : null
    })
    .filter((x): x is string => x != null)
  return parts.length > 0 ? parts.join(' · ') : null
}

function WeightDebugBody({ detail }: { detail: CaptureDetail }) {
  const traj = parseWeightTrajectory(detail.weightDebug)
  const measured = annNum(detail.annotation, 'measured_weight_grams')
  const guides = guideWeightEstimates(detail.annotation)
  const delta = detail.weightGrams != null && measured != null ? detail.weightGrams - measured : null
  return (
    <div className="px-4 py-3 space-y-3">
      <DebugBox title="Weight">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <ReadField label="Device" value={fmtWeight(detail.weightGrams)} />
          <ReadField label={guides ? 'Measured (guide mean)' : 'Measured'} value={fmtWeight(measured)} />
          {guides && <ReadField label="Guide estimates" value={guides} />}
          {delta != null && (
            <ReadField label="Δ" value={`${delta >= 0 ? '+' : ''}${fmtWeight(delta)} (${((delta / measured!) * 100).toFixed(1)}%)`} />
          )}
          <ReadField label="Trigger" value={detail.triggerSource ?? '—'} />
          {traj && (
            <ReadField
              label="Stable"
              value={traj.stable == null ? '—' : traj.stable
                ? `yes${traj.stableStddev != null ? ` · σ ${traj.stableStddev.toFixed(0)} g` : ''}`
                : 'no'}
            />
          )}
        </div>
      </DebugBox>
      {detail.weightDebug ? (
        <WeightDebugCharts detail={detail} />
      ) : (
        <p className="font-mono text-[10px] text-stone-400">no weight debug data</p>
      )}
      {detail.resultJsonPresignedUrl && (
        <a className="block font-mono text-[9px] text-stone-400 hover:text-stone-600 underline"
          href={detail.resultJsonPresignedUrl} target="_blank" rel="noopener noreferrer">
          view raw result.json →
        </a>
      )}
    </div>
  )
}

/** One row per device frame (RGB + depth), plus the on-net and server length overlays. */
function LengthDebugBody({ detail, onOpen }: { detail: CaptureDetail; onOpen: (idx: number, list: LightboxItem[]) => void }) {
  const images = detail.images ?? []
  const urlFor = (suffix: string) => images.find(i => i.s3Key.endsWith(suffix) && i.url)?.url ?? null
  const frameIdxs = Array.from(new Set(
    images.map(i => i.s3Key.match(/realsense_color_(\d+)\.jpg$/)?.[1]).filter((m): m is string => !!m)
  )).sort()
  const sl = detail.serverLength
  const slOk = sl?.status === 'OK'
  const measured = annNum(detail.annotation, 'measured_length_mm')
  const deviceChosen = typeof detail.chosenFrameIndex === 'number' ? detail.chosenFrameIndex : null
  // Most trusted first; % errors are against the first one that has a value.
  const lengths = [
    { label: 'Guide', mm: measured, sub: '' },
    {
      label: 'Fishial (server)',
      mm: slOk ? (sl?.lengthMillimeters ?? null) : null,
      sub: slOk && sl?.confidence != null ? `fish conf ${sl.confidence.toFixed(2)}` : sl?.status === 'NO_FISH' ? 'no fish' : '',
    },
    {
      label: 'Net',
      mm: detail.lengthMillimeters,
      sub: detail.lengthMethod
        ? `${detail.lengthMethod}${detail.lengthConfidence != null ? ` conf ${detail.lengthConfidence.toFixed(2)}` : ''}`
        : '',
    },
  ]
  const refIdx = lengths.findIndex(l => l.mm != null)

  const overlays = [
    { title: 'On-net', url: urlFor('length/annotated.png') },
    { title: 'Server (Fishial)', url: slOk ? (sl?.maskUrl ?? null) : null },
  ]
  const rows = frameIdxs.map(n => {
    const index = parseInt(n, 10)
    return {
      n,
      index,
      color: urlFor(`raw/realsense_color_${n}.jpg`),
      depth: urlFor(`raw/realsense_depth_vis_${n}.jpg`),
      depthRaw: urlFor(`raw/realsense_depth_raw_${n}.png`),
      server: sl?.frames.find(f => f.frameIndex === index) ?? null,
    }
  })
  // Captures computed before per-frame server results have no frames; skip the column.
  const hasServerFrames = (sl?.frames.length ?? 0) > 0
  const cols = hasServerFrames ? 'grid-cols-3' : 'grid-cols-2'
  // Lightbox walks overlays first, then each frame's images in row order.
  const list: LightboxItem[] = [
    ...overlays.map(o => o.url),
    ...rows.flatMap(r => [r.color, r.depth, r.server?.overlayUrl ?? null]),
  ]
    .filter((url): url is string => !!url)
    .map(url => ({ url, key: url, kind: 'image' }))
  const open = (url: string) => onOpen(list.findIndex(x => x.url === url), list)

  // Plain function (not a component) so images aren't remounted on every render.
  const img = (url: string | null, alt: string, empty = 'no image') => url ? (
    <img src={url} alt={alt} loading="lazy" onClick={() => open(url)}
      className="w-full rounded border border-stone-200 object-contain bg-stone-100 cursor-zoom-in" />
  ) : (
    <div className="w-full aspect-[4/3] rounded border border-dashed border-stone-200 flex items-center justify-center font-mono text-[9px] text-stone-400">
      {empty}
    </div>
  )

  return (
    <div className="px-4 py-3 space-y-3">
      <DebugBox title="Length">
        <table className="w-full font-mono text-xs text-stone-700">
          <tbody>
            {lengths.map((l, i) => (
              <tr key={l.label}>
                <td className="font-mono text-[9px] tracking-[0.15em] uppercase text-stone-500">{l.label}</td>
                <td className="text-[10px] text-stone-400">{l.sub}</td>
                <td className="text-right">{fmtLength(l.mm)}</td>
                <td className="text-right w-16 text-stone-500">
                  {i > refIdx && refIdx >= 0 && l.mm != null && pctErr(l.mm, lengths[refIdx].mm!)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {(detail.imageError || detail.lengthError) && (
          <div className="space-y-0.5 pt-0.5">
            {detail.imageError && <p className="font-mono text-[9px] text-coral">img: {detail.imageError}</p>}
            {detail.lengthError && <p className="font-mono text-[9px] text-coral">len: {detail.lengthError}</p>}
          </div>
        )}
      </DebugBox>

      <div className="grid grid-cols-2 gap-2">
        {overlays.map(o => (
          <div key={o.title} className="space-y-1">
            <div className="font-mono text-[9px] tracking-[0.15em] uppercase text-stone-400">{o.title}</div>
            {img(o.url, `${o.title} overlay`)}
          </div>
        ))}
      </div>

      {/* Fishial mask (white on black) -> 2px outline ring: luminance to alpha,
          dilate, subtract the original, paint white. Used on the depth images. */}
      <svg width="0" height="0" className="absolute" aria-hidden="true">
        <filter id="fishial-outline" x="0" y="0" width="100%" height="100%">
          <feColorMatrix in="SourceGraphic" type="luminanceToAlpha" result="fish" />
          <feMorphology in="fish" operator="dilate" radius="2" result="grown" />
          <feComposite in="grown" in2="fish" operator="out" result="ring" />
          <feFlood floodColor="white" />
          <feComposite in2="ring" operator="in" />
        </filter>
      </svg>

      {hasServerFrames && (
        <DebugBox title="Server, per frame">
          <table className="w-full font-mono text-[10px] text-stone-700">
            <thead>
              <tr className="text-[9px] uppercase tracking-[0.12em] text-stone-400 text-right">
                <th className="text-left font-normal">Frame</th>
                <th className="font-normal" title="The server's Fishial length for this frame: camera depth at every point along the midline">Fishial length</th>
                <th className="font-normal" title="The net's on-device length; the net measures only the frame it picked (ON-NET)">Net length</th>
                <th className="font-normal" title="Length with the whole fish placed at one distance: the median depth over the mask">Length, one depth</th>
                <th className="font-normal" title="That one distance: median camera-to-fish depth over the mask">Distance</th>
              </tr>
            </thead>
            <tbody>
              {sl!.frames.map(f => (
                <tr key={f.frameIndex}
                  className={`text-right ${f.midlineConfidence != null && f.midlineConfidence < MIDLINE_CONF_THRESHOLD ? 'text-stone-400' : ''}`}>
                  <td className="text-left">
                    {f.frameIndex}{f.frameIndex === sl!.chosenFrameIndex ? ' ★' : ''}
                  </td>
                  <td>{f.segmentationConfidence == null ? 'no fish' : fmtLength(f.lengthMillimeters)}</td>
                  <td>{f.frameIndex === deviceChosen ? fmtLength(detail.lengthMillimeters) : ''}</td>
                  <td>{fmtLength(f.medianDepthLengthMillimeters)}</td>
                  <td className="text-stone-400">{f.medianDepthMeters != null ? fmtLength(f.medianDepthMeters * 1000) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="font-mono text-[9px] text-stone-400">
            ★ the frame the server's length comes from · grey = the traced midline is too uncertain to use · hover a header for details
          </p>
        </DebugBox>
      )}

      <div className="space-y-2">
        <div className={`grid ${cols} gap-2 font-mono text-[9px] tracking-[0.15em] uppercase text-stone-400`}>
          <span>RGB</span><span>Depth</span>{hasServerFrames && <span>Server (Fishial)</span>}
        </div>
        {rows.length === 0 && <p className="font-mono text-[10px] text-stone-400">no frames</p>}
        {rows.map(r => (
          <div key={r.n} className="space-y-1">
            <div className="flex items-center gap-2 font-mono text-[10px] text-stone-600">
              <span>frame {r.index}</span>
              {r.index === deviceChosen && (
                <span className="text-[9px] uppercase tracking-[0.12em] bg-pine/10 text-pine px-1.5 py-0.5 rounded">on-net</span>
              )}
              {slOk && r.index === sl?.chosenFrameIndex && (
                <span className="text-[9px] uppercase tracking-[0.12em] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">server</span>
              )}
              {r.depthRaw && (
                <a href={r.depthRaw} target="_blank" rel="noopener noreferrer"
                  className="ml-auto text-[9px] text-stone-400 hover:text-stone-600 underline">raw depth</a>
              )}
            </div>
            <div className={`grid ${cols} gap-2`}>
              {img(r.color, `frame ${r.index} RGB`)}
              <div className="relative">
                {img(r.depth, `frame ${r.index} depth`)}
                {r.depth && r.server?.maskUrl && (
                  <svg className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true">
                    <image href={r.server.maskUrl} width="100%" height="100%" preserveAspectRatio="xMidYMid meet" filter="url(#fishial-outline)" />
                  </svg>
                )}
              </div>
              {hasServerFrames && img(r.server?.overlayUrl ?? null, `frame ${r.index} server overlay`, r.server?.segmentationConfidence == null ? 'no fish' : 'no image')}
            </div>
            {r.server && (
              <p className="font-mono text-[10px] text-stone-500">
                {r.server.segmentationConfidence == null ? 'no fish' : <>
                  fish conf {r.server.segmentationConfidence.toFixed(2)}
                  {r.server.midlineConfidence != null && <> · midline conf {r.server.midlineConfidence.toFixed(2)}</>}
                  {r.server.lengthMillimeters != null && <> · Fishial length <span className="text-stone-800">{fmtLength(r.server.lengthMillimeters)}</span></>}
                  {r.server.medianDepthLengthMillimeters != null && <> · one depth <span className="text-stone-800">{fmtLength(r.server.medianDepthLengthMillimeters)}</span></>}
                </>}
                {r.server.error && <span className="text-coral"> · {r.server.error}</span>}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
